import { Activity, BookOpen, Bot, Brain, Lock, ShieldCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AssistantMark } from '@/brand/BrandMarks.tsx'
import { HomeComposer } from '@/components/home/HomeComposer.tsx'
import { type HomeTile, HomeTiles } from '@/components/home/HomeTiles.tsx'
import { StarterChips } from '@/components/home/StarterChips.tsx'
import { readPreferredAgent, rememberPreferredAgent } from '@/components/marketplace/consumer/agent-choice.ts'
import { conversableAgents, pickAgent } from '@/components/marketplace/consumer/agent-identity.ts'
import { Button, EmptyState } from '@/components/ui/primitives.tsx'
import { useApprovals, useCreateConversation, useMemories, useSession, useUsage } from '@/hooks/use-api.ts'
import { useUsableKnowledgeBases } from '@/hooks/use-knowledge-bases.ts'
import { useAgentAbout, useGallery } from '@/hooks/use-marketplace.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Home. The root route, and the first thing anyone sees.
 *
 * `/` used to redirect into the newest conversation, and rendered something of its own only
 * when there was nothing to resume. It no longer redirects at all: resuming is one click in
 * the rail, which groups conversations by recency, and the screen that used to be a fallback
 * picker is now where the product introduces itself — a greeting, one composer, the chosen
 * agent's own starters, and the way into everything else.
 *
 * Three things the old page protected still hold, because the failures behind them have not
 * gone anywhere.
 *
 * **The conversation is created with the chosen `agentId`.** The pin is made at creation and
 * cannot be changed afterwards (AGM-007/F-10), so the agent is picked before the first message
 * rather than discovered after it. That is the entire reason the composer carries an agent
 * pill, and why Ask waits for the listing query to settle before it will start anything.
 *
 * **An empty gallery is not an unreachable one.** A successful listing with nothing in it is a
 * real state with a screen of its own: nobody has shared an agent with this person, and
 * quietly opening a conversation on a deployment default they were never shown and cannot
 * browse to would bury that. An unreachable listing (`gallery.isError`) is the opposite case
 * and still creates on the default agent, so a transient outage never strands anyone.
 *
 * **Nothing on this route navigates from an effect** — except the completion of an ask made
 * before the listing answered. The old `settled` ref existed because a redirect inside an
 * effect, plus a mutation object whose identity changes on every render, is an infinite
 * `navigate(…, { replace: true })` loop the app cannot escape. The ref went with the redirect;
 * the rule it enforced did not. Every navigation here starts from a press, and the one deferred
 * press is latched: the ref is cleared before anything is created, so it fires once, and
 * `galleryReady` turns true once — the same shape as the chat page's held message.
 *
 * The first-use notice (AGM-009/010) is deliberately not re-implemented here. Asking creates
 * the conversation and lands on it, and the chat surface raises the notice and blocks its own
 * composer until it is acknowledged. A third copy of it would be a third thing to keep correct.
 *
 * **Which agent is already chosen when you arrive** has three sources, in order (PRD-19
 * NAV-023/034): the agent the chat header's picker sent here in router state, the agent this
 * browser last chose, and the deployment default at the head of the listing. Every one of them
 * is validated against what this viewer can actually converse with, because the pin is
 * permanent and an id that no longer resolves is the one thing this screen must not create.
 *
 * What this screen refuses to draw: a number it did not measure — a count whose request has
 * not answered renders nothing rather than a zero — and any control the API cannot serve,
 * which is why the reference design's paperclip is absent.
 */
export const StartPage = () => {
  // In the body, not at module scope: a capture at import freezes the locale and this screen
  // would keep painting the previous language after a switch (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.home
  // The three sentences this screen borrows from the marketplace catalogue: the empty shelf
  // and the failure to start a conversation both belong to agents, not to home.
  const start = fmt.t.marketplace.gallery.start
  const navigate = useNavigate()
  const location = useLocation()
  const session = useSession()
  const gallery = useGallery()
  const create = useCreateConversation()
  const approvals = useApprovals()
  // Every base this person can use — the ones they maintain and the ones shared with them (PRD-26).
  const sources = useUsableKnowledgeBases()
  const memories = useMemories()

  const roles = session.data?.user?.roles ?? []
  const isOperator = roles.includes('operator') || roles.includes('admin')
  // Gated on the client as well as on the server: the Activity tile is the only thing that
  // could show this aggregate, and it is not drawn below operator. Firing it for everyone
  // would be a request whose answer this screen may never render.
  const usage = useUsage(30, isOperator)

  const composerRef = useRef<HTMLTextAreaElement>(null)
  const [draft, setDraft] = useState('')
  // Router state first — that is somebody pressing "talk to this one" in a chat header a moment
  // ago — then whatever this browser remembers. Read once, at mount: both are opening
  // conditions, and re-reading them would fight the picker on this very screen.
  const [preferred, setPreferred] = useState<string | null>(
    () => (location.state as { agentId?: string } | null)?.agentId ?? readPreferredAgent()
  )
  const [failed, setFailed] = useState(false)

  // Cleared when the user leaves this route. Creating a conversation is a round trip, and its
  // continuation still holds a live `navigate` after the component unmounts — so a person who
  // clicks a sidebar link mid-create was being yanked back into a chat they did not choose.
  // The newer user navigation wins: a stale redirect must defer to it.
  const active = useRef(true)
  useEffect(() => {
    // Re-armed on every mount, not just at declaration: StrictMode mounts, runs the cleanup,
    // then mounts again, so a ref cleared only in cleanup would stay false forever and no
    // conversation started from here would ever open.
    active.current = true
    return () => {
      active.current = false
    }
  }, [])

  /*
   * The screen exists to be typed into; landing with the cursor anywhere else would make the
   * first thing every visit asks for a click away.
   *
   * Keyed on `location.key` rather than mount, because "New conversation" is now a link to
   * this route and pressing it from here does not remount anything. React Router mints a new
   * key for a same-path navigation — a replace included — so this fires and the cursor goes
   * back where it belongs. Whatever was typed is deliberately left alone: the reader wrote it
   * seconds ago, and silently discarding it is a worse failure than a composer that keeps a
   * draft the reader can clear themselves.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: the navigation IS the event
  useEffect(() => {
    composerRef.current?.focus()
  }, [location.key])

  const options = gallery.data ? conversableAgents(gallery.data) : []
  const galleryReady = gallery.isError || !gallery.isLoading
  // The gallery came back and this person is in the audience of no conversable agent. Distinct
  // from an UNREACHABLE gallery, which also yields no options but falls back to the default
  // agent below — an empty *successful* load is a real state with its own screen, and
  // conflating the two hid that screen behind a spinner.
  const galleryEmpty = !gallery.isLoading && !gallery.isError && options.length === 0
  const chosen = pickAgent(options, preferred)

  // Remembered at the moment of choosing rather than at the moment of creating: a person who
  // picks an agent, reads its starters and then navigates away has still expressed a
  // preference, and coming back to the deployment default would read as the picker forgetting.
  const choose = (agentId: string) => {
    setPreferred(agentId)
    rememberPreferredAgent(agentId)
  }

  // The starters belong to the agent that is actually going to answer, so they follow the
  // pill rather than the deployment default.
  const about = useAgentAbout(chosen?.agentId)

  // `null` rather than a stand-in word when the session has named nobody. English fills that
  // hole with "there"; Portuguese has no such word and drops the address instead, which it
  // can only do if the absence itself reaches the catalogue (PRD-27 D-21).
  const displayName = session.data?.user?.displayName ?? null
  const firstName = displayName === null ? null : (displayName.split(' ')[0] ?? displayName)
  const greeting = t.greeting(new Date().getHours(), firstName)

  // An Enter pressed before the gallery answered: kept, and acted on once it has.
  const askWhenReady = useRef(false)

  const ask = async () => {
    const question = draft.trim()
    if (!question || create.isPending) return
    // `galleryReady` is part of the guard, not decoration: starting before the listing has
    // answered would pin the conversation to whatever the server considers default, which is
    // not necessarily the agent the pill is about to name — and the pin is permanent. So the
    // ask waits for it rather than starting early. It is not dropped either: the Ask button is
    // disabled meanwhile, but Enter is not, and a question typed and sent in the first moments
    // on the page used to stay in the box with nothing happening.
    if (!galleryReady) {
      askWhenReady.current = true
      return
    }
    askWhenReady.current = false

    setFailed(false)
    try {
      const created = await create.mutateAsync(chosen ? { agentId: chosen.agentId } : undefined)
      // Only navigate if the user is still here. If they moved on while the conversation was
      // being created, their choice outranks this one.
      if (!active.current) return
      // `autoSend` distinguishes this from a starter, which seeds the composer and is never
      // sent (AGM-005). What travels here is the reader's own words and their own press of
      // Ask, so the chat surface releases it — but only once the listing has resolved, which
      // is what keeps the first-use notice the thing that decides whether it goes.
      navigate(`/c/${created.id}`, { state: { prefill: question, autoSend: true } })
    } catch {
      setFailed(true)
    }
  }

  // The moment the gallery answers, a waiting ask goes — with whatever is in the box by then,
  // so a question cleared in the meantime sends nothing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `ask` is recreated each render; readiness is the event
  useEffect(() => {
    if (galleryReady && askWhenReady.current) void ask()
  }, [galleryReady])

  const pickStarter = (prompt: string) => {
    setDraft(prompt)
    composerRef.current?.focus()
  }

  if (galleryEmpty) {
    return (
      <div className="fade-in flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-5 pb-8 pt-1.5 sm:px-10">
        <Hero greeting={greeting} />
        <EmptyState
          title={start.empty.title}
          body={start.empty.body}
          action={
            <Link to="/agents">
              <Button size="sm">{start.browseAll}</Button>
            </Link>
          }
        />
      </div>
    )
  }

  /*
   * A floor, not a total. `GET /api/approvals` selects at most 50 rows and drops expired ones
   * in JS afterwards, so this saturates: with 63 waiting it reads 50, and clearing thirteen
   * of them moves nothing — which looks like a broken queue rather than a capped list. The
   * tile therefore renders `50+` at the cap and an exact number below it.
   */
  const APPROVALS_PAGE = 50
  const approvalRows = approvals.data?.items.length ?? 0
  const pendingApprovals = approvalRows
  const approvalsAtCap = approvalRows >= APPROVALS_PAGE
  const agentCount = gallery.data ? options.length : null
  const baseCount = sources.data ? sources.data.items.length : null
  // Summed from the per-base counts the listing already carries, over the bases this viewer is
  // allowed to read. There is no org-wide rollup on the API and this is not pretending to be
  // one: it is "documents you can read", which is what the tile says.
  const documentCount = sources.data
    ? sources.data.items.reduce((total, source) => total + source.documentCount, 0)
    : null

  const tiles: HomeTile[] = [
    {
      to: '/agents',
      icon: <Bot size={15} strokeWidth={1.9} aria-hidden />,
      ...t.tiles.agents,
      count: agentCount === null ? null : fmt.formatCount(agentCount),
      tone: 'primary'
    },
    {
      to: '/knowledge',
      icon: <BookOpen size={15} strokeWidth={1.9} aria-hidden />,
      ...t.tiles.knowledge,
      count: documentCount === null ? null : fmt.formatCount(documentCount),
      tone: 'grounded'
    },
    {
      to: '/memory',
      icon: <Brain size={15} strokeWidth={1.9} aria-hidden />,
      ...t.tiles.memory,
      count: memories.data ? fmt.formatCount(memories.data.items.length) : null,
      tone: 'primary'
    }
  ]

  // Approvals appear only when something is actually waiting: a permanent tile reading zero
  // teaches people to ignore the one place a decision is ever asked of them. It leads to the
  // queue rather than to a decision, because an approval is resolved in its conversation,
  // against the digest the approver was shown.
  if (pendingApprovals > 0) {
    tiles.push({
      to: '/governance',
      icon: <ShieldCheck size={15} strokeWidth={1.9} aria-hidden />,
      ...t.tiles.approvals,
      count: fmt.formatCount(pendingApprovals),
      countSuffix: approvalsAtCap ? '+' : undefined,
      tone: 'alert'
    })
  }

  if (isOperator) {
    tiles.push({
      to: '/activity',
      icon: <Activity size={15} strokeWidth={1.9} aria-hidden />,
      ...t.tiles.activity,
      count: usage.data ? fmt.formatCount(usage.data.totals.runs) : null,
      tone: 'primary'
    })
  }

  return (
    <div className="fade-in flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-5 pb-8 pt-1.5 sm:px-10 sm:pb-[30px]">
      <Hero greeting={greeting} standfirst={t.standfirst} />

      <StarterChips starters={about.data?.starters ?? []} onPick={pickStarter} />

      <HomeComposer
        agents={options}
        chosen={chosen}
        onChoose={choose}
        draft={draft}
        onDraft={setDraft}
        onAsk={() => void ask()}
        busy={create.isPending}
        ready={galleryReady}
        inputRef={composerRef}
      />

      {failed && (
        <p role="alert" className="mt-2.5 w-full max-w-[648px] text-[12.5px] leading-snug text-alert">
          {start.failed}
        </p>
      )}

      <HomeTiles tiles={tiles} />

      <p className="eyebrow mt-5 flex max-w-full items-center justify-center gap-[7px] text-[9.5px] tracking-[0.06em] text-fg-muted">
        <Lock size={11} strokeWidth={2.2} className="shrink-0 text-grounded" aria-hidden />
        <span className="text-center">{t.privacyLine(fmt, baseCount, agentCount)}</span>
      </p>
    </div>
  )
}

/** The orb's cast on the desk, in the brand colour so a re-brand carries it too. */
const HERO_HALO = 'radial-gradient(circle, color-mix(in srgb, var(--primary) 32%, transparent) 0%, transparent 66%)'

/**
 * The orb, the greeting and the one line under it.
 *
 * The orb is the only place on this screen the assistant itself appears — an agent wears its
 * own accent tile, and putting the machine's face on someone's listing would say the two are
 * the same thing. The halo behind it is a second, softer copy of the glow the sphere already
 * casts, which is what keeps a 98px object from looking pasted onto the desk.
 */
const Hero = ({ greeting, standfirst }: { greeting: string; standfirst?: string }) => (
  <>
    <div
      // `items-center justify-center`, because the mark inside is not necessarily square.
      // The orb is, and a deployment that supplies its own face need not be — Gi's is taller
      // than it is wide, and in a square box it hung five pixels left of the halo behind it.
      className="relative mt-8 flex h-[98px] w-[98px] items-center justify-center sm:mt-10"
      // Inline because the bob belongs to this one element on this one screen. Reduced motion
      // stills it through the global override in `surfaces.css`.
      style={{ animation: 'bob 7s ease-in-out infinite' }}
    >
      <span aria-hidden className="absolute -inset-[26px] rounded-full blur-[14px]" style={{ background: HERO_HALO }} />
      <AssistantMark hero size={98} />
    </div>

    <h1 className="rise mt-6 text-center text-[32px] tracking-[-0.022em] sm:text-[46px]">{greeting}</h1>

    {standfirst && (
      <p
        className="rise mt-2.5 max-w-[436px] text-pretty text-center text-[14.5px] leading-[1.55] text-fg-muted"
        style={{ animationDelay: '60ms' }}
      >
        {standfirst}
      </p>
    )}
  </>
)
